<template>
  <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
    <div class="sm:flex sm:items-center sm:justify-between mb-6">
      <div>
        <h1 class="text-2xl font-bold text-gray-900">Projects</h1>
        <p class="mt-1 text-sm text-gray-600">Manage your project portfolio</p>
      </div>
      <div class="mt-4 sm:mt-0">
        <button
          @click="showAddModal = true"
          class="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <svg class="-ml-1 mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
          </svg>
          Add Project
        </button>
      </div>
    </div>

    <!-- Controls -->
    <div class="mb-6 flex flex-col sm:flex-row gap-4">
      <!-- Global Search -->
      <div class="flex-1">
        <input
          :value="globalFilter"
          @input="debouncedFilterInput"
          type="text"
          placeholder="Search projects..."
          class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
        />
      </div>

      <!-- Active Filter -->
      <label class="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md bg-white text-sm text-gray-700 whitespace-nowrap">
        <input
          v-model="showActiveOnly"
          type="checkbox"
          class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
        />
        <span class="ml-2">Active only</span>
      </label>

      <!-- Column Visibility Dropdown -->
      <div class="relative">
        <button
          @click="showColumnMenu = !showColumnMenu"
          class="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
        >
          <svg class="-ml-1 mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
          </svg>
          Columns
        </button>

        <div
          v-if="showColumnMenu"
          class="absolute right-0 mt-2 w-48 bg-white border border-gray-300 rounded-md shadow-lg z-10"
        >
          <div class="p-3 space-y-2">
            <label v-for="column in table.getAllLeafColumns()" :key="column.id" class="flex items-center">
              <input
                type="checkbox"
                :checked="column.getIsVisible()"
                @change="column.toggleVisibility()"
                class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded cursor-pointer"
              />
              <span class="ml-2 text-sm text-gray-700">{{ column.columnDef.header }}</span>
            </label>
          </div>
        </div>
      </div>
    </div>

    <!-- Projects Table -->
    <div class="bg-white shadow-sm ring-1 ring-gray-900/5 sm:rounded-lg overflow-visible">
      <div class="px-4 py-3 border-b border-gray-200 bg-gray-50">
        <p class="text-xs text-gray-600">💡 Tip: Drag and drop rows to reorder projects</p>
      </div>
      <div v-if="loading" class="p-8 text-center text-gray-500">
        Loading projects...
      </div>

      <div v-else-if="error" class="p-8 text-center text-red-600">
        {{ error }}
      </div>

      <table v-else class="min-w-full divide-y divide-gray-300">
        <thead class="bg-gray-50">
          <tr v-for="headerGroup in table.getHeaderGroups()" :key="headerGroup.id">
            <th v-for="header in headerGroup.headers" :key="header.id" class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
              <span>{{ header.column.columnDef.header }}</span>
            </th>
          </tr>
        </thead>
        <tbody class="divide-y divide-gray-200 bg-white">
          <tr 
            v-for="row in table.getRowModel().rows" 
            :key="row.id" 
            draggable="true"
            @dragstart="handleDragStart(row.original)"
            @dragover.prevent="handleDragOver(row.original.id)"
            @dragleave="dragOverProjectId = null"
            @drop.prevent="handleDrop(row.original)"
            @dragend="draggedProjectId = null; dragOverProjectId = null"
            :class="[
              'transition-colors select-none cursor-move',
              draggedProjectId === row.original.id ? 'opacity-50 bg-blue-50' : 'hover:bg-gray-50'
            ]"
            :style="dragOverProjectId === row.original.id ? getDropIndicatorStyle() : {}"
          >
            <td v-for="cell in row.getVisibleCells()" :key="cell.id" class="py-4 pl-4 pr-3 text-sm sm:pl-6">
              <!-- Drag Handle -->
              <div v-if="cell.column.id === 'dragHandle'" class="flex items-center justify-center cursor-grab active:cursor-grabbing">
                <Bars3Icon class="h-5 w-5 text-gray-400 hover:text-gray-600" />
              </div>

              <!-- Project Name with Link -->
              <div v-else-if="cell.column.id === 'name'" class="flex items-center">
                <RouterLink
                  :to="`/projects/${row.original.id}`"
                  class="text-blue-600 hover:text-blue-900 font-medium"
                >
                  {{ cell.getValue() }}
                </RouterLink>
                <span
                  v-if="row.original.color"
                  class="inline-block h-2 w-2 rounded-full ml-3"
                  :style="{ backgroundColor: row.original.color }"
                ></span>
              </div>

              <!-- Description -->
              <div v-else-if="cell.column.id === 'description'" class="text-gray-500 max-w-xs truncate">
                {{ cell.getValue() || '—' }}
              </div>

              <!-- Dynamics ID Badge -->
              <div v-else-if="cell.column.id === 'dynamics_id'">
                <span class="inline-flex items-center rounded-md bg-blue-50 px-3 py-1 text-sm font-medium text-blue-700 ring-1 ring-inset ring-blue-600/20">
                  {{ cell.getValue() || '—' }}
                </span>
              </div>

              <!-- Status Badge -->
              <div v-else-if="cell.column.id === 'is_active'">
                <span
                  v-if="cell.getValue()"
                  class="inline-flex items-center rounded-full bg-green-50 px-2 py-1 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/20"
                >
                  Active
                </span>
                <span
                  v-else
                  class="inline-flex items-center rounded-full bg-gray-50 px-2 py-1 text-xs font-medium text-gray-600 ring-1 ring-inset ring-gray-500/10"
                >
                  Archived
                </span>
              </div>

              <!-- Tags -->
              <div v-else-if="cell.column.id === 'tags'">
                <div v-if="cell.getValue()?.length" class="flex flex-wrap gap-2">
                  <span
                    v-for="tag in cell.getValue()"
                    :key="tag.id"
                    class="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium"
                    :style="{ backgroundColor: tag.color + '22', color: tag.color }"
                  >
                    <span class="inline-block h-2 w-2 rounded-full mr-1" :style="{ backgroundColor: tag.color }"></span>
                    {{ tag.label }}
                  </span>
                </div>
                <span v-else class="text-xs text-gray-400">—</span>
              </div>

              <!-- Date -->
              <div v-else-if="cell.column.id === 'created_at'" class="text-gray-500">
                {{ formatDate(cell.getValue()) }}
              </div>

              <!-- Actions Dropdown -->
              <div v-else-if="cell.column.id === 'actions'" class="relative">
                <button
                  :data-project-id="row.original.id"
                  @click="toggleActionMenu(row.original.id)"
                  class="inline-flex items-center justify-center h-8 w-8 rounded-md text-gray-400 hover:text-gray-600"
                >
                  <EllipsisVerticalIcon class="h-5 w-5" />
                </button>

                <Teleport to="body">
                  <div
                    v-if="activeActionMenu === row.original.id"
                    data-dropdown-menu
                    class="fixed bg-white border border-gray-300 rounded-md shadow-lg z-50"
                    :style="getDropdownPosition(row.original.id)"
                  >
                    <button
                      @click="editProject(row.original)"
                      class="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 first:rounded-t-md"
                    >
                      Edit
                    </button>
                    <button
                      @click="toggleArchive(row.original)"
                      class="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      {{ row.original.is_active ? 'Archive' : 'Unarchive' }}
                    </button>
                    <button
                      @click="confirmDelete(row.original)"
                      class="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 last:rounded-b-md"
                    >
                      Delete
                    </button>
                  </div>
                </Teleport>
              </div>

              <!-- Default Cell -->
              <div v-else>
                {{ cell.getValue() }}
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- Empty State -->
      <div v-if="!loading && table.getRowModel().rows.length === 0" class="p-8 text-center text-gray-500">
        No projects found.
      </div>
    </div>

    <!-- Add/Edit Project Modal -->
    <div v-if="showAddModal || editingProject" class="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
      <div class="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
        <h3 class="text-lg font-medium text-gray-900 mb-4">
          {{ editingProject ? 'Edit Project' : 'Add New Project' }}
        </h3>
        <form @submit.prevent="saveProject">
          <div class="space-y-4">
            <div>
              <label for="name" class="block text-sm font-medium text-gray-700">Name *</label>
              <input
                v-model="form.name"
                type="text"
                id="name"
                required
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm px-3 py-2 border text-black"
              />
            </div>
            <div>
              <label for="dynamics_id" class="block text-sm font-medium text-gray-700">Dynamics ID *</label>
              <input
                v-model="form.dynamics_id"
                type="text"
                id="dynamics_id"
                placeholder="e.g., A7K2N9P5"
                minlength="4"
                maxlength="12"
                required
                @input="form.dynamics_id = form.dynamics_id.toUpperCase()"
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm px-3 py-2 border text-black"
              />
              <p class="mt-1 text-xs text-gray-500">4-12 alphanumeric characters (auto-uppercase)</p>
            </div>
            <div>
              <label for="description" class="block text-sm font-medium text-gray-700">Description</label>
              <textarea
                v-model="form.description"
                id="description"
                rows="3"
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm px-3 py-2 border text-black"
              ></textarea>
            </div>
            <div>
              <label for="color" class="block text-sm font-medium text-gray-700">Color</label>
              <input
                v-model="form.color"
                type="color"
                id="color"
                class="mt-1 block h-10 w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              />
            </div>
          </div>
          <div class="mt-6 flex justify-end space-x-3">
            <button
              type="button"
              @click="cancelEdit"
              class="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              class="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
            >
              {{ editingProject ? 'Save' : 'Create' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import {
  createColumnHelper,
  getCoreRowModel,
  getFilteredRowModel,
  useVueTable
} from '@tanstack/vue-table'
import { EllipsisVerticalIcon, Bars3Icon } from '@heroicons/vue/24/outline'
import { getProjects, createProject as apiCreateProject, updateProject, deleteProject, deleteProjectPermanently, getProjectTags, getAllProjectTags } from '@/composables/api'

// Debounce utility function
function debounce(func, delay) {
  let timeoutId
  return function(...args) {
    clearTimeout(timeoutId)
    timeoutId = setTimeout(() => func.apply(this, args), delay)
  }
}

// Debounced filter input (300ms delay for search)
const debouncedFilterInput = debounce((e) => {
  globalFilter.value = e.target.value
}, 300)

// Tag cache to avoid refetching after reorder operations
let tagsCacheByProjectId = {}

const projects = ref([])
const loading = ref(true)
const error = ref(null)
const showAddModal = ref(false)
const showColumnMenu = ref(false)
const editingProject = ref(null)
const activeActionMenu = ref(null)
const globalFilter = ref('')
const draggedProjectId = ref(null)
const dragOverProjectId = ref(null)
const showActiveOnly = ref(true)

const visibleProjects = computed(() => {
  if (!showActiveOnly.value) return projects.value
  return projects.value.filter(project => Boolean(project.is_active))
})

const form = ref({
  name: '',
  description: '',
  dynamics_id: '',
  color: '#3B82F6'
})

const columnHelper = createColumnHelper()

const columns = [
  columnHelper.display({
    id: 'dragHandle',
    cell: () => {
      return '⋮⋮'
    },
    header: '',
    enableColumnFilter: false,
    size: 40
  }),
  columnHelper.accessor('name', {
    id: 'name',
    cell: info => {
      const val = info.getValue()
      return val ? val : '—'
    },
    header: 'Name',
    enableColumnFilter: true,
    size: 200
  }),
  columnHelper.accessor('dynamics_id', {
    id: 'dynamics_id',
    cell: info => {
      const val = info.getValue()
      return val ? val : '—'
    },
    header: 'Dynamics ID',
    enableColumnFilter: true,
    size: 140
  }),
  columnHelper.accessor('description', {
    id: 'description',
    cell: info => {
      const val = info.getValue()
      return val ? val : '—'
    },
    header: 'Description',
    enableColumnFilter: true,
    size: 300
  }),
  columnHelper.accessor('is_active', {
    id: 'is_active',
    cell: info => {
      const val = info.getValue()
      return val ? 'Active' : 'Inactive'
    },
    header: 'Status',
    enableColumnFilter: false,
    size: 100
  }),
  columnHelper.accessor('tags', {
    id: 'tags',
    cell: info => info.getValue(),
    header: 'Tags',
    enableColumnFilter: false,
    size: 220
  }),
  columnHelper.accessor('created_at', {
    id: 'created_at',
    cell: info => {
      const val = info.getValue()
      if (!val) return '—'
      try {
        return new Date(val).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        })
      } catch {
        return '—'
      }
    },
    header: 'Created',
    enableColumnFilter: false,
    size: 150
  }),
  columnHelper.display({
    id: 'actions',
    header: 'Actions',
    size: 100
  })
]

const table = useVueTable({
  get data() {
    return visibleProjects.value
  },
  columns,
  state: {
    get globalFilter() {
      return globalFilter.value
    }
  },
  onGlobalFilterChange: (value) => {
    globalFilter.value = value
  },
  globalFilterFn: (row, columnId, filterValue) => {
    const searchTerm = filterValue.toLowerCase()
    
    // Search in project name
    if (row.original.name?.toLowerCase().includes(searchTerm)) {
      return true
    }
    
    // Search in project description
    if (row.original.description?.toLowerCase().includes(searchTerm)) {
      return true
    }
    
    // Search in project tags
    if (row.original.tags && row.original.tags.length > 0) {
      return row.original.tags.some(tag => 
        tag.label?.toLowerCase().includes(searchTerm)
      )
    }
    
    return false
  },
  getCoreRowModel: getCoreRowModel(),
  getFilteredRowModel: getFilteredRowModel()
})

async function loadProjects() {
  try {
    loading.value = true
    error.value = null
    const response = await getProjects()
    const projectsList = response.projects || []

    // Bulk fetch all project tags in one query (performance optimization)
    const tagsResponse = await getAllProjectTags()
    const tagsByProject = tagsResponse.tagsByProject || {}

    // Cache tags for later use (e.g., avoiding refetch after reorder)
    tagsCacheByProjectId = { ...tagsByProject }

    projects.value = projectsList.map(project => ({
      ...project,
      tags: tagsByProject[project.id] || []
    }))
  } catch (err) {
    error.value = err.message
  } finally {
    loading.value = false
  }
}

function editProject(project) {
  activeActionMenu.value = null
  editingProject.value = project
  form.value = {
    name: project.name,
    description: project.description || '',
    dynamics_id: project.dynamics_id || '',
    color: project.color || '#3B82F6'
  }
}

function cancelEdit() {
  showAddModal.value = false
  editingProject.value = null
  form.value = { name: '', description: '', dynamics_id: '', color: '#3B82F6' }
}

async function saveProject() {
  try {
    if (editingProject.value) {
      await updateProject(editingProject.value.id, form.value)
    } else {
      await apiCreateProject(form.value)
    }
    cancelEdit()
    await loadProjects()
  } catch (err) {
    alert('Failed to save project: ' + err.message)
  }
}

async function toggleArchive(project) {
  activeActionMenu.value = null
  try {
    await updateProject(project.id, { is_active: project.is_active ? 0 : 1 })
    await loadProjects()
  } catch (err) {
    alert('Failed to update project: ' + err.message)
  }
}

async function confirmDelete(project) {
  activeActionMenu.value = null
  if (confirm(`Are you sure you want to permanently delete "${project.name}"? This action cannot be undone. All associated milestones, deadlines, and tasks will be deleted.`)) {
    try {
      await deleteProjectPermanently(project.id)
      await loadProjects()
    } catch (err) {
      alert('Failed to delete project: ' + err.message)
    }
  }
}

function toggleActionMenu(projectId) {
  activeActionMenu.value = activeActionMenu.value === projectId ? null : projectId
}

function getDropdownPosition(projectId) {
  // Find the button element for this project
  const button = document.querySelector(`[data-project-id="${projectId}"]`)
  if (!button) {
    return { top: '0px', left: '0px' }
  }

  const rect = button.getBoundingClientRect()
  // Position dropdown below the button with a small gap
  return {
    top: (rect.bottom + window.scrollY + 8) + 'px',
    left: (rect.right - 200 + window.scrollX) + 'px', // 200px is dropdown width, align to right
    width: '200px'
  }
}

function formatDate(dateString) {
  if (!dateString) return '—'
  try {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  } catch (err) {
    console.error('Error formatting date:', dateString, err)
    return '—'
  }
}

function handleDragStart(project) {
  draggedProjectId.value = project.id
}

function handleDragOver(projectId) {
  dragOverProjectId.value = projectId
}

function getDropIndicatorClass() {
  if (!draggedProjectId.value || !dragOverProjectId.value) return ''
  
  const draggedIndex = projects.value.findIndex(p => p && p.id === draggedProjectId.value)
  const targetIndex = projects.value.findIndex(p => p && p.id === dragOverProjectId.value)
  
  if (draggedIndex === -1 || targetIndex === -1) return ''
  
  // If dragging from above (draggedIndex < targetIndex), show bottom border
  // If dragging from below (draggedIndex > targetIndex), show top border
  if (draggedIndex < targetIndex) {
    return 'border-b-4 border-blue-500 !border-b-4'
  } else {
    return 'border-t-4 border-blue-500 !border-t-4'
  }
}

function getDropIndicatorStyle() {
  if (!draggedProjectId.value || !dragOverProjectId.value) return {}
  
  const draggedIndex = projects.value.findIndex(p => p && p.id === draggedProjectId.value)
  const targetIndex = projects.value.findIndex(p => p && p.id === dragOverProjectId.value)
  
  if (draggedIndex === -1 || targetIndex === -1) return {}
  
  if (draggedIndex < targetIndex) {
    // Show line at bottom
    return {
      backgroundImage: 'linear-gradient(to bottom, transparent 95%, rgb(59, 130, 246) 95%, rgb(59, 130, 246) 100%)',
      backgroundSize: '100% 100%',
      backgroundPosition: '0 0',
      backgroundRepeat: 'no-repeat'
    }
  } else {
    // Show line at top
    return {
      backgroundImage: 'linear-gradient(to bottom, rgb(59, 130, 246) 0%, rgb(59, 130, 246) 5%, transparent 5%)',
      backgroundSize: '100% 100%',
      backgroundPosition: '0 0',
      backgroundRepeat: 'no-repeat'
    }
  }
}

async function handleDrop(targetProject) {
  if (!draggedProjectId.value || draggedProjectId.value === targetProject.id || projects.value.length === 0) {
    draggedProjectId.value = null
    dragOverProjectId.value = null
    return
  }

  const draggedIndex = projects.value.findIndex(p => p && p.id === draggedProjectId.value)
  const targetIndex = projects.value.findIndex(p => p && p.id === targetProject.id)

  if (draggedIndex === -1 || targetIndex === -1) {
    draggedProjectId.value = null
    dragOverProjectId.value = null
    return
  }

  try {
    // Remove the dragged project from its current position
    const [draggedProject] = projects.value.splice(draggedIndex, 1)

    // Insert it at the target position
    // If dragged from above, targetIndex is still valid
    // If dragged from below, targetIndex shifts by 1 after removal
    const insertIndex = draggedIndex < targetIndex ? targetIndex - 1 : targetIndex
    projects.value.splice(insertIndex, 0, draggedProject)

    // Update sort_order for all projects based on their new positions
    const updatePromises = projects.value.map((project, index) => {
      if (project) {
        return updateProject(project.id, { 
          name: project.name, 
          sort_order: index 
        })
      }
    }).filter(Boolean)

    await Promise.all(updatePromises)
    
    // Instead of reloading all projects with tags, just reload the list and use cached tags
    // This avoids refetching tags unnecessarily
    try {
      const response = await getProjects()
      const projectsList = response.projects || []
      
      projects.value = projectsList.map(project => ({
        ...project,
        tags: tagsCacheByProjectId[project.id] || []
      }))
    } catch {
      // If projects list fails to load, fall back to full reload
      await loadProjects()
    }
  } catch (err) {
    console.error('Failed to update sort order:', err)
    alert('Failed to update sort order: ' + err.message)
    // Reload to sync with server
    await loadProjects()
  } finally {
    draggedProjectId.value = null
    dragOverProjectId.value = null
  }
}

onMounted(() => {
  loadProjects()
  
  // Close dropdown when clicking outside
  document.addEventListener('click', (e) => {
    // Check if clicked on any project button or inside any dropdown menu
    const clickedButton = e.target.closest('[data-project-id]')
    const clickedDropdown = e.target.closest('[data-dropdown-menu]')
    
    // Only close if we didn't click on a button or dropdown
    if (!clickedButton && !clickedDropdown) {
      activeActionMenu.value = null
    }
  })
})
</script>
